import React, { useEffect, useState } from "react";
import { AccessCredentialsScopeName } from "../../../../api/entities/access-credential";
import { IPage } from "../../../../api/entities/page";
import { IShardRepositoryRaw, IShardRepository, ShardRepositorySortField } from "../../../../api/entities/shard-repository";
import { IShardClusterDetailsItem } from "../../../../api/entities/shard-cluster";
import { shardRepositoryService } from "../../../../api/services/shard-repository-service";
import { formUtils, IFormField, useField } from "../../../../utils/form-utils";
import { useApi } from "../../../../utils/hooks/use-api";
import { storageTypeToNameMap, storageUtils } from "../../../../utils/storage-utils";
import Button from "../../../shared-ui/button";
import { CompactTable } from "../../../shared-ui/compact-table/compact-table";
import DetailsPanel from "../../../shared-ui/details-panel/details-panel";
import { FormPanel } from "../../../shared-ui/form/form-panel/form-panel";
import { IColumnDefinition } from "../../../shared-ui/table/table";
import { getStoragePoolNodes } from "../../../buckets/sections/pool-storage-nodes-info-section/pool-storage-nodes-info-section";
import { ShardRepositoryPanel, ShardRepositoryPanelMode } from "../../../shard-repositories/panels/shard-repository-panel/shard-repository-panel";
import { CredentialsInfo } from "../../dialogs/credentials-info/credentials-info";
import { IShardRepositoryTypes } from "../../../shard-repositories/sections/shard-repository-section/shard-repository-section";
import styles from "./shard-cluster-credentials-update-panel.module.scss";

export interface IShardClusterCredentialsUpdatePanelProps {
    open: boolean;
    onClose: () => void;
    onSaveShardClusterRepositories: (shardClusterRepositories?: IShardClusterRepositoryCredentialStatus[]) => void;

    shardCluster: IShardClusterDetailsItem;
    accessCredentialsScope: AccessCredentialsScopeName;
}

export interface IShardClusterRepositoryCredentialStatus {
    shardRepository: IShardRepository;
    shardRepositoryDetails: IShardRepositoryRaw;
    types: IShardRepositoryTypes;
    needCredentials: boolean;
    testConnection: boolean;
}

interface IShardClusterRepositoriesCredentialStatusForm {
    shardClusterRepositories: IFormField<IShardClusterRepositoryCredentialStatus[]>;
}

interface IShardClusterRepositoriesCredentialStatusFormValue {
    shardClusterRepositories: IShardClusterRepositoryCredentialStatus[];
}

export const ShardClusterCredentialsUpdatePanel = (props: IShardClusterCredentialsUpdatePanelProps) => {
    const { open, shardCluster } = props;

    const [api, isLoading] = useApi();

    const form: IShardClusterRepositoriesCredentialStatusForm = {
        shardClusterRepositories: useField<IShardClusterRepositoryCredentialStatus[]>([])
    };

    const columns: IColumnDefinition<IShardClusterRepositoryCredentialStatus, null>[] = [
        {
            sortKey: ShardRepositorySortField.Name,
            label: "Bucket / Container",
            className: styles["col--storage-node-name"],
            formatter: row =>
                <Button testId="name-button" className={styles["node-link"]} colors="link" onClick={() => handleShardRepositorySensitiveFieldsUpdate(row)}>
                    {row.shardRepository.bucket}
                </Button>
        } as IColumnDefinition<IShardClusterRepositoryCredentialStatus, null>,
        {
            sortKey: ShardRepositorySortField.Type,
            label: "Provider",
            className: styles["col--storage-node-provider"],
            formatter: row => storageTypeToNameMap[storageUtils.getStorageProviderTypeGeneralizedType(row.shardRepository.storageType)]
        } as IColumnDefinition<IShardClusterRepositoryCredentialStatus, null>,
        {
            label: "Credential",
            className: styles["col--storage-node-credential"],
            formatter: row => row.needCredentials ?
                <span className={styles["col--storage-node-credential-missing"]}>Need credential</span> :
                row.testConnection ?
                    <span className={styles["col--storage-node-credential-ok"]}>OK</span> :
                    <span className={styles["col--storage-node-credential-test-connection-failed"]}>Test Connection failed</span>
        } as IColumnDefinition<IShardClusterRepositoryCredentialStatus, null>,
    ];

    const [shardRepositoryPanelMode, setShardRepositoryPanelMode] = useState<ShardRepositoryPanelMode>(null);
    const [selectedShardRepository, setSelectedShardRepository] = useState<IShardClusterRepositoryCredentialStatus>(null);

    const handleShardRepositorySensitiveFieldsUpdate = (storageClusterRepository: IShardClusterRepositoryCredentialStatus) => {
        setShardRepositoryPanelMode(ShardRepositoryPanelMode.SensitiveFieldsUpdate);
        setSelectedShardRepository(storageClusterRepository);
    };
    const handleCloseShardRepositoryPanel = () => {
        setShardRepositoryPanelMode(null);
        setSelectedShardRepository(null);
    }

    function loadData() {
        if (!!props.shardCluster) {
            api(shardRepositoryService.getPage({ offset: 0, pool: props.shardCluster.id }))
                .then((storageNodePage: IPage<IShardRepository>) => {
                    let shardClusterRepositories = getStoragePoolNodes(shardCluster, storageNodePage)
                        .map(x => ({
                            ...x,
                            shardRepositoryDetails: null,
                            needCredentials: true,
                            testConnection: true
                        }));
                    formUtils.setFormValue<IShardClusterRepositoriesCredentialStatusFormValue>(form, { shardClusterRepositories });
                });
        }
    }

    useEffect(() => {
        if (!!open) {
            loadData();
        }
        // eslint-disable-next-line
    }, [open]);

    return (
        <>
            <DetailsPanel
                isLoading={false}
                open={open}
                onCloseClick={props.onClose}
                title={`Shard Cluster: ${shardCluster?.name}`}
                className={styles.panel}
            >
                {open && (
                    <FormPanel
                        okButtonText="Next"
                        okButtonClick={() => props.onSaveShardClusterRepositories(form.shardClusterRepositories.value)}
                        okButtonDisabled={form.shardClusterRepositories.value.some(x => x.needCredentials || !x.testConnection)}
                    >
                        <CompactTable
                            className={styles.table}
                            columns={columns}
                            rows={form.shardClusterRepositories.value}
                            loading={isLoading}
                            testId="storage-node-credentials-status-table"
                        />
                    </FormPanel>
                )}
            </DetailsPanel>

            <ShardRepositoryPanel
                mode={shardRepositoryPanelMode}
                shardClusterRepository={selectedShardRepository}
                onClose={handleCloseShardRepositoryPanel}
                onRepositorySaved={(shardRepository: IShardRepositoryRaw, _, testConnectionResult: boolean) => {
                    let updatedStoragePoolNode = form.shardClusterRepositories.value.find(x => x.shardRepository.id === shardRepository.id);
                    updatedStoragePoolNode.shardRepositoryDetails = shardRepository;
                    updatedStoragePoolNode.needCredentials = false;
                    updatedStoragePoolNode.testConnection = testConnectionResult;

                    form.shardClusterRepositories.onChange(form.shardClusterRepositories.value);
                }}
                accessCredentialsScope={props.accessCredentialsScope}
            />

            <CredentialsInfo />
        </>
    );
}
